import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { api, setToken, clearToken } from "../api";

interface User {
  id: number;
  email: string;
  name: string;
  is_admin: boolean;
  photo_url?: string | null;
}

interface AuthCtx {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, name: string, password: string) => Promise<void>;
  logout: () => void;
  setUser: (user: User) => void;
}

const USER_CACHE_KEY = "novacare.user";

const Ctx = createContext<AuthCtx>(null!);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return;
    }
    // Show cached user immediately to avoid "Demo" flicker on refresh.
    try {
      const cached = localStorage.getItem(USER_CACHE_KEY);
      if (cached) setUser(JSON.parse(cached));
    } catch {
      // ignore corrupt cache
    }
    api
      .me()
      .then((u) => {
        setUser(u);
        try { localStorage.setItem(USER_CACHE_KEY, JSON.stringify(u)); } catch {}
      })
      .catch(() => {
        clearToken();
        localStorage.removeItem(USER_CACHE_KEY);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email: string, password: string) => {
    const data = await api.login(email, password);
    setToken(data.access_token);
    setUser(data.user);
    try { localStorage.setItem(USER_CACHE_KEY, JSON.stringify(data.user)); } catch {}
  };

  const register = async (email: string, name: string, password: string) => {
    const data = await api.register(email, name, password);
    setToken(data.access_token);
    setUser(data.user);
    try { localStorage.setItem(USER_CACHE_KEY, JSON.stringify(data.user)); } catch {}
  };

  const logout = () => {
    clearToken();
    localStorage.removeItem(USER_CACHE_KEY);
    setUser(null);
  };

  const setUserAndCache = (u: User) => {
    setUser(u);
    try { localStorage.setItem(USER_CACHE_KEY, JSON.stringify(u)); } catch {}
  };

  return (
    <Ctx.Provider value={{ user, loading, login, register, logout, setUser: setUserAndCache }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  return useContext(Ctx);
}